import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import debounce from 'lodash/debounce'
import styled from 'styled-components'
import Spinner from 'britive-design-system/core/components/spinner'
import Snackbar from 'britive-design-system/core/components/snackbar'
import Table from 'britive-design-system/core/components/table'
import Typography from 'britive-design-system/core/components/typography'
import Pill from 'britive-design-system/core/components/pill'
import Button from 'britive-design-system/core/components/button'
import ModalPopup from 'britive-design-system/core/components/modal-popup'

import { getPaginatedUsers } from 'action_creators/user'
import getTotalPageCount from 'utils/getTotalPageCount'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .cell-with-icon {
    display: flex;
    justify-content: space-between;
    .cell-icon {
      cursor: pointer;
    }
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`

// Main UsersModal component
const UsersModal = ({
  type = 'users',
  onActiveSelectedRow,
  selectedManagerID,
  selectedManagerName,
  memberRules = [],
}) => {
  const [selectedUser, setSelectedUser] = useState(null)
  const {
    paginatedUsers = {},
    fetchingPaginatedUsers,
    error: userError,
  } = useSelector(state => state.admin)

  const dispatch = useDispatch()
  useEffect(() => {
    if (!selectedManagerID) return
    if (!Array.isArray(paginatedUsers?.data)) return

    if (selectedManagerID) {
      const selectedUser = paginatedUsers?.data?.find(
        user => user.userId === selectedManagerID
      )
      if (selectedUser) {
        setSelectedUser(selectedUser)
      } else {
        setSelectedUser({ name: selectedManagerName, userId: selectedManagerID })
      }
    }
  }, [selectedManagerID, paginatedUsers?.data])

  const membersHelper = {
    users: {
      fetching: fetchingPaginatedUsers,
      fetched: fetchingPaginatedUsers,
      error: userError,
      fetchData: params =>
        dispatch(getPaginatedUsers(params, paginatedUsers?.searchText)),
      getData: () =>
        (paginatedUsers?.data || []).filter(
          user => !memberRules?.some(memberRule => memberRule.id === user.userId)
        ),
      paginatedData: paginatedUsers,
      filterData: (data, value) => {
        return data.filter(
          ({ name, username, email }) =>
            name.toLowerCase().includes(value.toLowerCase()) ||
            username.toLowerCase().includes(value.toLowerCase()) ||
            email.toLowerCase().includes(value.toLowerCase())
        )
      },
      columns: [
        {
          headerName: 'Name',
          field: 'name',
          width: 100,
          renderColumn: row => {
            return <div style={{ display: 'flex' }}>{row.name}</div>
          },
        },
        {
          headerName: 'Username',
          field: 'username',
          width: 60,
        },
        {
          headerName: 'Email',
          field: 'email',
          width: 80,
        },
      ],
    },
  }

  const {
    fetching,
    error,
    fetchData,
    getData,
    columns,
    paginatedData,
  } = membersHelper[type]

  useEffect(() => {
    if (isEmpty(paginatedData?.data) || paginatedData?.searchText) {
      fetchData({
        page: 0,
        size: 100,
      })
    }
  }, [])

  const data = useMemo(() => getData(), [paginatedUsers?.data, memberRules])

  const handleLoadMore = () => {
    fetchData({
      page: paginatedData?.page + 1,
      size: 100,
    })
  }

  const handleSearch = value => {
    fetchData({
      page: 0,
      size: 100,
      searchText: value?.length ? value : '',
    })
  }

  const tableProps = {
    rows: data,
    columns,
    resizableColumns: true,
    searchBar: {
      onSearch: debounce(handleSearch, 300),
      placeholder: 'Search',
    },
    allowRowSelect: true,
    highlightedRow: selectedUser,
    onRowSelect: row => {
      setSelectedUser(row)
      onActiveSelectedRow(row)
    },
  }

  return (
    <Container>
      {fetching && <Spinner size={'medium'} message="Loading..." overlay />}
      {error && (
        <Snackbar errorList={[`Something went wrong while fetching ${type}!`]} />
      )}
      {
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography variant="heading6">Selected Manager :</Typography>
            {selectedUser?.name ? (
              <Pill
                label={selectedUser?.name}
                key={selectedUser?.name}
                onDelete={() => {
                  setSelectedUser(null)
                  onActiveSelectedRow(null)
                }}
              />
            ) : (
              <Typography variant="label1">None</Typography>
            )}
          </div>
          <Table {...tableProps} />
          {!data.length && !fetching && (
            <Typography variant="heading6">No {type} found!</Typography>
          )}
          {!fetching &&
            data?.length > 0 &&
            paginatedData?.page <
              getTotalPageCount(paginatedData?.count, paginatedData?.size) && (
              <ButtonContainer>
                <Button variant="textOnly" size="medium" onClick={handleLoadMore}>
                  Load More
                </Button>
              </ButtonContainer>
            )}
        </>
      }
    </Container>
  )
}

export default UsersModal

// Reusable Modal: Select Manager
export const UserManagerModal = ({
  open,
  onClose,
  onActiveSelectedRow,
  selectedManagerID,
  selectedManagerName,
  buttons,
}) => {
  if (!open) return null
  return (
    <ModalPopup
      width={712}
      title="Select Manager"
      buttons={buttons}
      onCancel={onClose}
    >
      <UsersModal
        onActiveSelectedRow={onActiveSelectedRow}
        selectedManagerID={selectedManagerID}
        selectedManagerName={selectedManagerName}
      />
    </ModalPopup>
  )
}
